import { z } from 'zod';
import {
  booleanOperators,
  customFieldPicklistOperators,
  datetimeOperators,
  numericOperators,
  picklistOperators,
  stringOperators,
  textCustomFieldOperators,
} from './operators';

export const datetimeFilter = z
  .object({
    value: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .optional(),
    operator: datetimeOperators,
  })
  .optional();

export const picklistFilter = z
  .object({
    value: z
      .union([z.number(), z.array(z.number())])
      .nullable()
      .optional(),
    operator: picklistOperators,
  })
  .optional();

export const codeFilter = z
  .object({
    value: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .optional(),
    operator: picklistOperators,
  })
  .optional();

export const booleanFilter = z
  .object({
    value: z.boolean().nullable().optional(),
    operator: booleanOperators,
  })
  .optional();

const textCustomFieldFilter = z.object({
  value: z.string().nullable().optional(),
  operator: textCustomFieldOperators.default('containsIgnoreCase'),
});

const linkCustomFieldFilter = z.object({
  value: z.string().nullable().optional(),
  operator: stringOperators,
});

const picklistTypeCustomFieldFilter = z.object({
  value: z
    .union([z.coerce.number(), z.array(z.coerce.number())])
    .nullable()
    .optional(),
  operator: customFieldPicklistOperators,
});

const itemIdCustomFieldFilter = z.object({
  value: z
    .union([z.coerce.number(), z.array(z.coerce.number())])
    .nullable()
    .optional(),
  operator: picklistOperators,
});

const customerIdCustomFieldFilter = z.object({
  value: z
    .union([z.coerce.number(), z.array(z.coerce.number())])
    .nullable()
    .optional(),
  operator: picklistOperators,
});

const userCustomFieldFilter = z.object({
  value: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
  operator: picklistOperators,
});

const dateCustomFieldFilter = z.object({
  value: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
  operator: datetimeOperators,
});

const booleanCustomFieldFilter = z.object({
  value: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
  operator: picklistOperators,
});

const numericCustomFieldFilter = z.object({
  value: z
    .union([z.coerce.number(), z.array(z.coerce.number())])
    .nullable()
    .optional(),
  operator: numericOperators,
});

const CF_KEY_PATTERN = /^cf\.(\d+)\.(\w+)$/;

const customFieldFilterByType: Record<string, z.ZodType> = {
  text: textCustomFieldFilter,
  link: linkCustomFieldFilter,
  picklist: picklistTypeCustomFieldFilter,
  itemId: itemIdCustomFieldFilter,
  userId: userCustomFieldFilter,
  customerId: customerIdCustomFieldFilter,
  checked: booleanCustomFieldFilter,
  date: dateCustomFieldFilter,
  number: numericCustomFieldFilter,
  currency: numericCustomFieldFilter,
};

export const parseCustomFieldSearchParams = (
  search: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    const match = key.match(CF_KEY_PATTERN);
    if (!match?.[2]) continue;
    const cfType = match[2];
    const schema = customFieldFilterByType[cfType];
    if (!schema) continue;
    const parsed = schema.safeParse(value);
    if (parsed.success) {
      result[key] = parsed.data;
    }
  }
  return result;
};
